// client/src/theme/tokens.js
//
// Shared design tokens for the Bozarth Connect frontend.
// Keep visual constants (brand colors, radii, spacing) here so components
// don't hard-code hex values inline. Import what you need, e.g.:
//   import { colors, radii } from '../../theme/tokens';

export const colors = {
  // Brand navy — matches the header gradient start (#1b3378).
  navy: '#1b3378',
  navyDark: '#132143',
  navyLight: '#2a4796',

  // Neutrals
  white: '#ffffff',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray400: '#9ca3af',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',

  // Accents
  red600: '#dc2626',
  red50: '#fef2f2',
};

export const radii = {
  sm: '0.375rem', // 6px
  md: '0.5rem',   // 8px
  lg: '0.75rem',  // 12px
  full: '9999px',
};

export const spacing = {
  gap: '1.5rem', // 24px — standard header flex gap
};

export const avatar = {
  size: 64, // px — navy circular avatar diameter
};

/* ---------------------------------------------------------------------------
   Responsive breakpoints

   Single source of truth for the three dashboard layout tiers. Everything that
   needs to branch on viewport width — Tailwind's `screens` (see
   tailwind.config.js, which imports these), plain CSS media queries in
   App.css, and JS layout logic — must derive from here rather than repeating
   a magic number.

     compact   <  1024px   phones / narrow tablets
     standard  1024–1439px laptops, the common case
     wide      >= 1440px   large desktop, widest layout

   Values are the tier's *minimum* width in px, matching the mobile-first
   min-width convention Tailwind uses. 1024 is deliberately Tailwind's default
   `lg`, so `lg:` and the `standard` tier stay the same boundary.
   --------------------------------------------------------------------------- */
export const breakpoints = {
  compact: 0,
  standard: 1024,
  wide: 1440,
};

/** Tier names ordered narrowest → widest. */
export const breakpointOrder = ['compact', 'standard', 'wide'];

/**
 * Ready-made media query strings for JS `matchMedia` and CSS-in-JS.
 * Each entry matches exactly one tier — the ranges do not overlap.
 */
export const mediaQueries = {
  compact: `(max-width: ${breakpoints.standard - 1}px)`,
  standard: `(min-width: ${breakpoints.standard}px) and (max-width: ${breakpoints.wide - 1}px)`,
  wide: `(min-width: ${breakpoints.wide}px)`,

  // Open-ended helpers, for "this and up" / "this and down" rules.
  standardUp: `(min-width: ${breakpoints.standard}px)`,
  belowWide: `(max-width: ${breakpoints.wide - 1}px)`,
};

/**
 * Resolve a viewport width (px) to its tier name.
 * Pure so layout logic stays testable without a DOM.
 *
 * @param {number} width viewport width in px
 * @returns {'compact'|'standard'|'wide'}
 */
export function resolveBreakpoint(width) {
  if (width >= breakpoints.wide) return 'wide';
  if (width >= breakpoints.standard) return 'standard';
  return 'compact';
}

export default {
  colors,
  radii,
  spacing,
  avatar,
  breakpoints,
  breakpointOrder,
  mediaQueries,
  resolveBreakpoint,
};
